/* eslint-disable import/no-named-as-default-member */
import { BoxProps } from '@mui/material'
import { FlexGrowRow } from '@xylabs/sdk-react'
import { Quadkey } from '@xyo-network/sdk-xyoworld-js'
// eslint-disable-next-line no-restricted-imports
import MapBox from 'mapbox-gl'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { AppSettingsContext, PlayMapSettings, useApi } from '../../Contexts'
import { XyoWorldMapStyle } from '../../lib'
import { GeotokenFillLayer, GeotokenSource } from './Geotoken'
import MapSettings from './MapSettings'
import { QuadkeyFillLayer, QuadkeySource, QuadkeySymbolLayer } from './Quadkey'
import TilePlayView from './TilePlayView'

MapBox.accessToken = 'pk.eyJ1IjoicGFvbGFiYXRpeiIsImEiOiJjazdsM3pmNmswM293M2VtbnphNjF3YjI5In0.xYVfJRVJSRCJRBTBF6wRUA'

const GridMap: React.FC<BoxProps> = (props) => {
  const { ...boxProps } = props
  const api = useApi()
  const { mapStyle, requestLocation, playMapSettings } = useContext(AppSettingsContext)
  const [map, setMap] = useState<MapBox.Map>()
  const [settings, setSettings] = useState(playMapSettings)

  const [selectedQuadkey, setSelectedQuadkey] = useState<Quadkey>()

  console.log(`selectedQuadkey: ${selectedQuadkey?.toBase4HashLabel()}`)

  const quadkeyFillLayer = useRef(new QuadkeyFillLayer('quadkey-fill-layer', 'quadkey'))
  const quadkeySymbolLayer = useRef(new QuadkeySymbolLayer('quadkey-symbol-layer', 'quadkey'))
  const geotokenFillLayer = useRef(new GeotokenFillLayer('geotoken-fill-layer', 'geotoken'))
  const geotokenSource = useRef(new GeotokenSource('geotoken'))
  const quadkeySource = useRef(new QuadkeySource('quadkey'))

  const updateQuadkeys = useCallback((map: MapBox.Map, settings: PlayMapSettings) => {
    console.log(`settings: ${JSON.stringify(settings)}`)
    if (map) {
      quadkeySource.current.update(map)
      quadkeyFillLayer.current.update(map, settings?.grid)
      quadkeySymbolLayer.current.update(map, settings?.quadkeys, settings?.tileVectors)
    }
  }, [])

  const updateGeotokens = useCallback(
    async (map: MapBox.Map, settings: PlayMapSettings) => {
      console.log(`settings: ${JSON.stringify(settings)}`)
      if (api && map) {
        if (settings.geotokens) {
          await geotokenSource.current.update(map, api, true)
          geotokenFillLayer.current.update(map, true)
        } else {
          geotokenFillLayer.current.update(map, false)
          await geotokenSource.current.update(map, api, false)
        }
      }
    },
    [api],
  )

  const update = useCallback(
    async (map: MapBox.Map, settings: PlayMapSettings) => {
      updateQuadkeys(map, settings)
      await updateGeotokens(map, settings)
    },
    [updateQuadkeys, updateGeotokens],
  )

  const onSettingsChange = async (newSettings: PlayMapSettings) => {
    console.log(`onSettingsChange: ${JSON.stringify(settings)}, ${map}`)
    if (map) {
      if (newSettings.quadkeys !== settings?.quadkeys || newSettings.grid !== settings?.grid || newSettings.tileVectors !== settings?.tileVectors) {
        updateQuadkeys(map, newSettings)
      }
      if (newSettings.geotokens !== settings?.geotokens) {
        await updateGeotokens(map, newSettings)
      }
    }
    setSettings(newSettings)
  }

  const mapContainerRef = useRef<HTMLDivElement | null>(null)

  const onZoomEnd = useCallback(
    async (map: MapBox.Map, settings?: PlayMapSettings) => {
      if (settings) {
        await update(map, settings)
      }
    },
    [update],
  )

  const onDragEnd = useCallback(
    async (map: MapBox.Map, settings?: PlayMapSettings) => {
      if (settings) {
        await update(map, settings)
      }
    },
    [update],
  )

  const onLoad = useCallback(
    async (map: MapBox.Map, settings?: PlayMapSettings) => {
      if (settings) {
        await update(map, settings)
      }
    },
    [update],
  )

  const onQuadkeyClick = useCallback(
    (
      event: MapBox.MapMouseEvent & {
        features?: MapBox.MapboxGeoJSONFeature[] | undefined
      } & MapBox.EventData,
    ) => {
      const quadkey = event.features?.[0].properties?.quadkey as string
      setSelectedQuadkey(Quadkey.fromBase4String(quadkey))
    },
    [],
  )

  const zoom = 2

  useEffect(() => {
    if (playMapSettings && api) {
      const settings = playMapSettings
      console.log('Creating Map')

      const geolocateControl = new MapBox.GeolocateControl({
        fitBoundsOptions: {
          zoom,
        },
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      })

      const map = new MapBox.Map({
        center: [0, 0],
        container: mapContainerRef.current ?? '',
        style: `mapbox://styles/${mapStyle ?? XyoWorldMapStyle.Streets}`,
        zoom,
      })
      map.on('load', async () => {
        await onLoad(map, settings)
      })
      if (requestLocation) {
        map.addControl(geolocateControl)
      }
      map.addControl(
        new MapBox.NavigationControl({
          showCompass: false,
        }),
      )
      setMap(map)
      return () => {
        console.log('Removing Map')
        map.remove()
      }
    }
  }, [mapContainerRef, mapStyle, onLoad, playMapSettings, api, requestLocation, onQuadkeyClick])

  useEffect(() => {
    if (map && settings) {
      map.on('zoomend', async () => {
        await onZoomEnd(map, settings)
      })
      map.on('dragend', async () => {
        await onDragEnd(map, settings)
      })
      map.on('click', 'grid-qk-polygons-layer', (event) => {
        onQuadkeyClick(event)
      })
    }
  }, [onZoomEnd, onDragEnd, map, settings, onQuadkeyClick])

  return (
    <FlexGrowRow position="relative" {...boxProps}>
      <div ref={(el) => (mapContainerRef.current = el)} style={{ bottom: 0, left: 0, position: 'absolute', right: 0, top: 0 }} />
      <TilePlayView quadkey={selectedQuadkey} />
      <MapSettings onChange={(settings: PlayMapSettings) => onSettingsChange(settings)} />
    </FlexGrowRow>
  )
}

export default GridMap
